import type { ClinicSource } from '../services/api/types.gen';
import { clinicParticipatesInKinnser } from './clinicKeyUtils';
import {
  shouldShowClinicEmrPills,
  type ClinicEmrAgencyFlags,
} from './clinicEmr';

export type IntakeSchedulingEmr = 'roger' | 'kinnser';

interface AgencyClinicLike {
  id: string;
  clinic_source?: ClinicSource | string;
  is_kinnser?: boolean;
  /** When true, dual-capable Extraction defaults to RogerEMR. */
  default_intake_emr_is_roger?: boolean;
}

export interface SchedulingEmrFlags {
  roger_create_episode?: boolean | null;
  kinnser_create_episode?: boolean | null;
  push_to_roger?: boolean | null;
  push_to_kinnser?: boolean | null;
  /** Shared visit-type key; the header sets it to 'rec' (Schedule REC in Roger) or null. */
  kinnser_visit_type?: 'soc' | 'roc' | 'rec' | null;
}

export type ExtractionEmrControlState = {
  /** Agency has both EMRs — render the control (layout may place it beside Clinic). */
  show: boolean;
  disabled: boolean;
  options: IntakeSchedulingEmr[];
  placeholder: string;
};

/**
 * UI state for the Extraction Clinic|EMR row EMR control (agency-gated).
 *
 * - No clinic: disabled, both options listed, “Select clinic first”
 * - Roger-only: disabled, RogerEMR only (auto-selected elsewhere)
 * - Kinnser-participating: enabled, both options; clinic Admin default when unset
 */
export function getExtractionEmrControlState(
  clinic: AgencyClinicLike | null | undefined,
  agency: ClinicEmrAgencyFlags | null | undefined
): ExtractionEmrControlState {
  if (!shouldShowClinicEmrPills(agency)) {
    return {
      show: false,
      disabled: true,
      options: [],
      placeholder: 'Select Kinnser or RogerEMR',
    };
  }

  if (!clinic) {
    return {
      show: true,
      disabled: true,
      options: ['kinnser', 'roger'],
      placeholder: 'Select clinic first',
    };
  }

  if (clinicParticipatesInKinnser(clinic)) {
    return {
      show: true,
      disabled: false,
      options: ['kinnser', 'roger'],
      placeholder: 'Select Kinnser or RogerEMR',
    };
  }

  return {
    show: true,
    disabled: true,
    options: ['roger'],
    placeholder: 'RogerEMR',
  };
}

/**
 * Agency has both EMRs and the clinic participates in Kinnser.
 * Extraction shows an EMR dropdown; unset flags use the clinic's default EMR.
 */
export function isDualCapableClinic(
  clinic: AgencyClinicLike | null | undefined,
  agency: ClinicEmrAgencyFlags | null | undefined
): boolean {
  if (!clinic) return false;
  return (
    shouldShowClinicEmrPills(agency) && clinicParticipatesInKinnser(clinic)
  );
}

/**
 * Clinic-configured Extraction EMR default for dual-capable clinics.
 * External/Kinnser remains the product default when the flag is unset/false.
 */
export function getDefaultIntakeEmrForClinic(
  clinic: AgencyClinicLike | null | undefined
): IntakeSchedulingEmr {
  return clinic?.default_intake_emr_is_roger === true ? 'roger' : 'kinnser';
}

/** Read explicit EMR choice from persisted intake push/schedule flags. */
export function getSelectedSchedulingEmrFromFlags(
  flags: SchedulingEmrFlags | null | undefined
): IntakeSchedulingEmr | null {
  if (!flags) return null;
  if (isSchedulingEnabledForEmr(flags, 'roger')) return 'roger';
  if (isSchedulingEnabledForEmr(flags, 'kinnser')) return 'kinnser';
  return null;
}

/**
 * Resolve which EMR scheduling destination applies for the intake's selected clinic.
 *
 * Dual-capable clinics: use explicit intake push/schedule flags; otherwise the
 * clinic's Admin default (external/Kinnser unless default_intake_emr_is_roger).
 * Single-path: Kinnser-participating → kinnser, else roger.
 */
export function getSchedulingEmrForClinic(
  agencyClinics: AgencyClinicLike[],
  agencyClinicId: string | null | undefined,
  options?: {
    agency?: ClinicEmrAgencyFlags | null;
    flags?: SchedulingEmrFlags | null;
  }
): IntakeSchedulingEmr | null {
  if (!agencyClinicId) return null;

  const clinic = agencyClinics.find(c => c.id === agencyClinicId);
  if (!clinic) return null;

  if (isDualCapableClinic(clinic, options?.agency ?? null)) {
    return (
      getSelectedSchedulingEmrFromFlags(options?.flags) ??
      getDefaultIntakeEmrForClinic(clinic)
    );
  }

  if (clinicParticipatesInKinnser(clinic)) return 'kinnser';
  return 'roger';
}

/**
 * Whether RogerEMR-only intake features apply to this intake.
 *
 * The master switch is the Internal Admin "Roger EMR" agency toggle
 * (`is_rogeremr_enabled`). That alone is not sufficient: a dual-EMR agency has
 * the toggle on while individual intakes still go to Kinnser, so there the
 * intake's resolved clinic EMR must agree. Agencies without Kinnser skip the
 * clinic check entirely, so the toggle behaves as the only gate for them —
 * including before a clinic has been selected.
 *
 * Returns `null` when the answer is not knowable yet: a dual-EMR agency whose
 * clinics are still being fetched. Callers must not collapse that to `false`
 * and render the Kinnser path — the clinics arrive a moment later and the swap
 * would remount the form under the user. Wait instead.
 *
 * `clinicsSettled` must say whether the clinic fetch has finished (successfully
 * or not), not whether the list came back non-empty. An agency can legitimately
 * have zero clinics, and a failed fetch leaves it empty forever; treating
 * either as "still loading" would strand the caller on a spinner with no retry.
 */
export function usesRogerEmrForIntake(
  agency: ClinicEmrAgencyFlags | null | undefined,
  agencyClinics: AgencyClinicLike[],
  agencyClinicId: string | null | undefined,
  flags?: SchedulingEmrFlags | null,
  clinicsSettled: boolean = true
): boolean | null {
  if (agency?.is_rogeremr_enabled !== true) return false;
  if (agency?.is_kinnser_enabled !== true) return true;

  // Dual-EMR: the clinic decides, so an in-flight fetch is "don't know" rather
  // than "not Roger". A settled-but-empty list resolves to Kinnser below, same
  // as any clinic we cannot match.
  if (!clinicsSettled) return null;

  return (
    getSchedulingEmrForClinic(agencyClinics, agencyClinicId, {
      agency,
      flags,
    }) === 'roger'
  );
}

/** Whether the intake already has scheduling enabled for the given EMR. */
export function isSchedulingEnabledForEmr(
  flags: SchedulingEmrFlags,
  emr: IntakeSchedulingEmr
): boolean {
  if (emr === 'roger') {
    return flags.roger_create_episode === true || flags.push_to_roger === true;
  }
  return (
    flags.push_to_kinnser === true || flags.kinnser_create_episode === true
  );
}

/**
 * Build persisted intake fields for auto-selecting scheduling from clinic source.
 * Clears the opposite EMR's flags so Roger and Kinnser are never peer choices.
 *
 * `kinnser_create_episode` is only present when this patch owns it (Roger
 * selection clears it; a new-patient Kinnser selection asserts it). For an
 * existing/undecided patient on a Kinnser clinic it is omitted: the visit-type
 * selection owns it there (`getVisitTypePatch` sets it true for SOC), and
 * asserting `false` here clobbered that choice on every sync, making the SOC
 * new-admission auto-add flow unreachable.
 */
export type SchedulingEmrSelectionPatch = Required<
  Pick<
    SchedulingEmrFlags,
    'roger_create_episode' | 'push_to_roger' | 'push_to_kinnser'
  >
> &
  Pick<SchedulingEmrFlags, 'kinnser_create_episode' | 'kinnser_visit_type'>;

/** Compare persisted/form flags to the clinic-source patch (null/undefined → false). */
export function schedulingFlagsMatchPatch(
  flags: SchedulingEmrFlags,
  patch: SchedulingEmrSelectionPatch
): boolean {
  return (
    Object.entries(patch) as Array<
      [keyof SchedulingEmrSelectionPatch, boolean | string | null]
    >
  )
    .filter((entry): entry is [keyof SchedulingEmrSelectionPatch, boolean] => {
      return typeof entry[1] === 'boolean';
    })
    .every(([field, value]) => Boolean(flags[field]) === value);
}

/**
 * REC is Roger-only: any patch that moves scheduling off Roger must clear a
 * stale 'rec' visit type, or the MRN resolver and REC form gating keep keying
 * off it while the Kinnser flow renders.
 */
function clearsStaleRec(
  currentVisitType: string | null | undefined
): Pick<SchedulingEmrFlags, 'kinnser_visit_type'> {
  return currentVisitType === 'rec' ? { kinnser_visit_type: null } : {};
}

export function buildSchedulingEmrSelectionPatch(
  emr: IntakeSchedulingEmr,
  isNewPatient: boolean | null | undefined,
  currentVisitType?: string | null
): SchedulingEmrSelectionPatch {
  if (emr === 'roger') {
    return {
      roger_create_episode: true,
      push_to_roger: true,
      push_to_kinnser: false,
      kinnser_create_episode: false,
    };
  }

  return {
    push_to_kinnser: true,
    push_to_roger: false,
    roger_create_episode: false,
    ...(isNewPatient === true ? { kinnser_create_episode: true } : {}),
    ...clearsStaleRec(currentVisitType),
  };
}

/** Clear EMR choice (legacy). Prefer clinic default for dual clinics instead. */
function buildClearSchedulingEmrSelectionPatch(
  currentVisitType?: string | null
): SchedulingEmrSelectionPatch {
  return {
    roger_create_episode: false,
    push_to_roger: false,
    push_to_kinnser: false,
    kinnser_create_episode: false,
    ...clearsStaleRec(currentVisitType),
  };
}

/**
 * EMR flag patch when the Extraction clinic changes.
 * Dual-capable → clinic Admin default; single-path → clinic EMR; unknown → clear.
 */
export function buildSchedulingEmrPatchForClinicChange(
  agencyClinics: AgencyClinicLike[],
  agencyClinicId: string | null | undefined,
  agency: ClinicEmrAgencyFlags | null | undefined,
  isNewPatient: boolean | null | undefined,
  currentVisitType?: string | null
): SchedulingEmrSelectionPatch {
  const emr = getSchedulingEmrForClinic(agencyClinics, agencyClinicId, {
    agency,
    // Clinic change: take clinic default, ignore prior flags.
    flags: null,
  });
  if (!emr) return buildClearSchedulingEmrSelectionPatch(currentVisitType);
  return buildSchedulingEmrSelectionPatch(emr, isNewPatient, currentVisitType);
}
